import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  DocumentDuplicateIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  PencilIcon,
  CheckIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import { mediaApi } from '../../services/mediaApi';
import toast from 'react-hot-toast';

interface MediaItem {
  id: number;
  uuid: string;
  filename: string;
  title: string;
  caption?: string;
  alt_text?: string;
  category: string;
  folder_id?: number;
  tags?: string[];
  original_path: string;
  optimized_path?: string;
  thumbnail_path?: string;
  width: number;
  height: number;
  original_size: number;
  optimized_size?: number;
  uploaded_at: string;
  usage_count: number;
  mime_type?: string;
}

interface MediaDetailsProps {
  media: MediaItem;
  onClose: () => void;
  onUpdate: () => void;
  onDelete?: () => void;
}

export default function MediaDetails({
  media,
  onClose,
  onUpdate,
  onDelete
}: MediaDetailsProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState({
    title: media.title,
    caption: media.caption || '',
    alt_text: media.alt_text || '',
    tags: media.tags?.join(', ') || '',
    category: media.category
  });
  const [isSaving, setIsSaving] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [urlVersion, setUrlVersion] = useState<'original' | 'optimized' | 'thumbnail'>('optimized');

  useEffect(() => {
    setEditedData({
      title: media.title,
      caption: media.caption || '',
      alt_text: media.alt_text || '',
      tags: media.tags?.join(', ') || '',
      category: media.category
    });
  }, [media]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const updateData = {
        title: editedData.title,
        caption: editedData.caption,
        alt_text: editedData.alt_text,
        tags: editedData.tags.split(',').map(t => t.trim()).filter(t => t),
        category: editedData.category
      };

      console.log('Sending update request for media ID:', media.id);
      console.log('Update data:', updateData);

      await mediaApi.update(media.id, updateData);

      toast.success('Media updated successfully!');
      setIsEditing(false);
      onUpdate();
    } catch (error: any) {
      console.error('Failed to update media:', error);
      console.error('Error response:', error.response);

      // Show detailed error message
      const errorMessage = error.response?.data?.detail ||
                          error.response?.data?.message ||
                          error.message ||
                          'Failed to update media';

      toast.error(`Update failed: ${errorMessage}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setEditedData({
      title: media.title,
      caption: media.caption || '',
      alt_text: media.alt_text || '',
      tags: media.tags?.join(', ') || '',
      category: media.category
    });
    setIsEditing(false);
  };

  const copyUrl = (url: string, type: string) => {
    const fullUrl = `https://admin.clarityofconcept.com${url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopiedUrl(type);
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const copyBaseUrl = () => {
    const url = `https://admin.clarityofconcept.com/api/media/file/${media.uuid}`;
    navigator.clipboard.writeText(url);
    setCopiedUrl('base');
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const copyUnifiedUrl = () => {
    let url = `https://admin.clarityofconcept.com/api/media/file/${media.uuid}`;
    // Only append version for non-default versions (original/thumbnail)
    if (urlVersion !== 'optimized') {
      url += `/${urlVersion}`;
    }
    navigator.clipboard.writeText(url);
    setCopiedUrl('unified');
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const handleDownload = () => {
    const url = `https://admin.clarityofconcept.com${media.original_path}`;
    const link = document.createElement('a');
    link.href = url;
    link.download = media.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const getCategoryColor = (category: string): string => {
    switch (category) {
      case 'map':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400';
      case 'diagram':
        return 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400';
      case 'news':
        return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-400';
      case 'quiz':
        return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400';
      default:
        return 'bg-gray-100 text-gray-700 dark:bg-gray-900/20 dark:text-gray-400';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Media Details</h2>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                title="Edit"
              >
                <PencilIcon className="w-5 h-5" />
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="p-2 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                  title="Save"
                >
                  <CheckIcon className="w-5 h-5" />
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="p-2 text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                  title="Cancel"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </>
            )}
            <button
              onClick={handleDownload}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              title="Download"
            >
              <ArrowDownTrayIcon className="w-5 h-5" />
            </button>
            {onDelete && (
              <button
                onClick={onDelete}
                className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                title="Delete"
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-6">
            {/* Preview */}
            <div>
              <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
                Preview
              </h3>
              <div className="bg-gray-100 dark:bg-gray-900 rounded-lg p-4">
                {media.optimized_path || media.original_path ? (
                  <img
                    src={`https://admin.clarityofconcept.com${media.optimized_path || media.original_path}`}
                    alt={media.alt_text || media.title}
                    className="w-full rounded-lg"
                  />
                ) : (
                  <div className="aspect-video flex items-center justify-center">
                    <PhotoIcon className="w-16 h-16 text-gray-400" />
                  </div>
                )}
              </div>

              {/* Base Unified URL - Always shows clean URL */}
              <div className="mt-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Base Unified URL
                </h4>
                <div className="flex items-center gap-2">
                  <div className="flex-1 p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <code className="text-xs text-gray-600 dark:text-gray-400 break-all">
                      https://admin.clarityofconcept.com/api/media/file/{media.uuid}
                    </code>
                  </div>
                  <button
                    onClick={copyBaseUrl}
                    className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                  >
                    {copiedUrl === 'base' ? (
                      <CheckIcon className="w-4 h-4" />
                    ) : (
                      <DocumentDuplicateIcon className="w-4 h-4" />
                    )}
                    Copy
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Use this URL in your database. Defaults to optimized version.
                </p>
              </div>

              {/* Version-Specific URLs */}
              <div className="mt-4 space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Version-Specific URLs</h4>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <select
                      value={urlVersion}
                      onChange={(e) => setUrlVersion(e.target.value as 'original' | 'optimized' | 'thumbnail')}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="optimized">Optimized (Default)</option>
                      <option value="original">Original</option>
                      <option value="thumbnail">Thumbnail</option>
                    </select>
                    <button
                      onClick={copyUnifiedUrl}
                      className="px-3 py-1.5 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                    >
                      {copiedUrl === 'unified' ? (
                        <CheckIcon className="w-4 h-4" />
                      ) : (
                        <DocumentDuplicateIcon className="w-4 h-4" />
                      )}
                      Copy Version URL
                    </button>
                  </div>
                  <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                    <code className="text-xs text-gray-600 dark:text-gray-400 break-all">
                      https://admin.clarityofconcept.com/api/media/file/{media.uuid}{urlVersion !== 'optimized' ? `/${urlVersion}` : ''}
                    </code>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Select a version above to see its specific URL.
                  </p>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="space-y-6">
              {/* Metadata */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
                  Metadata
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Title
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.title}
                        onChange={(e) => setEditedData({ ...editedData, title: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    ) : (
                      <p className="text-gray-900 dark:text-gray-100">{media.title}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Caption
                    </label>
                    {isEditing ? (
                      <textarea
                        value={editedData.caption}
                        onChange={(e) => setEditedData({ ...editedData, caption: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    ) : (
                      <p className="text-gray-900 dark:text-gray-100">
                        {media.caption || <span className="text-gray-500">No caption</span>}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Alt Text
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.alt_text}
                        onChange={(e) => setEditedData({ ...editedData, alt_text: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    ) : (
                      <p className="text-gray-900 dark:text-gray-100">
                        {media.alt_text || <span className="text-gray-500">No alt text</span>}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category
                    </label>
                    {isEditing ? (
                      <select
                        value={editedData.category}
                        onChange={(e) => setEditedData({ ...editedData, category: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      >
                        <option value="general">General</option>
                        <option value="map">Map</option>
                        <option value="diagram">Diagram</option>
                        <option value="news">News</option>
                        <option value="quiz">Quiz</option>
                      </select>
                    ) : (
                      <span className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${getCategoryColor(media.category)}`}>
                        {media.category}
                      </span>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Tags
                    </label>
                    {isEditing ? (
                      <input
                        type="text"
                        value={editedData.tags}
                        onChange={(e) => setEditedData({ ...editedData, tags: e.target.value })}
                        placeholder="Comma separated tags"
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {media.tags && media.tags.length > 0 ? (
                          media.tags.map((tag, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded-full"
                            >
                              {tag}
                            </span>
                          ))
                        ) : (
                          <span className="text-gray-500">No tags</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Technical Details */}
              <div>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">
                  Technical Details
                </h3>
                <dl className="space-y-2">
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Filename</dt>
                    <dd className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {media.filename}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Dimensions</dt>
                    <dd className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {media.width} × {media.height} px
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Original Size</dt>
                    <dd className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatFileSize(media.original_size)}
                    </dd>
                  </div>
                  {media.optimized_size && (
                    <div className="flex justify-between">
                      <dt className="text-sm text-gray-500 dark:text-gray-400">Optimized Size</dt>
                      <dd className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {formatFileSize(media.optimized_size)}
                        <span className="text-green-600 ml-1">
                          (-{Math.round((1 - media.optimized_size / media.original_size) * 100)}%)
                        </span>
                      </dd>
                    </div>
                  )}
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500 dark:text-gray-400">MIME Type</dt>
                    <dd className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {media.mime_type || 'Unknown'}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Uploaded</dt>
                    <dd className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {formatDate(media.uploaded_at)}
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-sm text-gray-500 dark:text-gray-400">Usage Count</dt>
                    <dd className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {media.usage_count} times
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}